/**
 * Environment configuration — centralizes all backend endpoint URLs and
 * optional bearer token for production deployments.
 *
 * All env vars are resolved at build time through Vite's import.meta.env
 * substitution. Unset values fall back to relative paths so the dev-server
 * proxy in vite.config.ts transparently forwards requests.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

// ─── Raw env lookup (Vite-compatible, also safe in jsdom) ────────────────────

const viteEnv: Record<string, unknown> = {
  DEV: import.meta.env.DEV,
  MODE: import.meta.env.MODE,
  VITE_CHART_API_BASE: import.meta.env.VITE_CHART_API_BASE,
  VITE_GPKG_SERVER_BASE: import.meta.env.VITE_GPKG_SERVER_BASE,
  VITE_API_TOKEN: import.meta.env.VITE_API_TOKEN,
  VITE_CESIUM_ION_TOKEN: import.meta.env.VITE_CESIUM_ION_TOKEN,
  VITE_GOOGLE_MAPS_KEY: import.meta.env.VITE_GOOGLE_MAPS_KEY,
  VITE_GPKG_COLLECTION_AIRPORTS: import.meta.env.VITE_GPKG_COLLECTION_AIRPORTS,
  VITE_GPKG_COLLECTION_RUNWAYS: import.meta.env.VITE_GPKG_COLLECTION_RUNWAYS,
  VITE_GPKG_COLLECTION_COMMUNICATIONS: import.meta.env.VITE_GPKG_COLLECTION_COMMUNICATIONS,
  VITE_GPKG_COLLECTION_AIRSPACES: import.meta.env.VITE_GPKG_COLLECTION_AIRSPACES,
  VITE_GPKG_COLLECTION_NAVAIDS: import.meta.env.VITE_GPKG_COLLECTION_NAVAIDS,
  VITE_GPKG_COLLECTION_OBSTACLES: import.meta.env.VITE_GPKG_COLLECTION_OBSTACLES,
  VITE_GPKG_COLLECTION_FIXES: import.meta.env.VITE_GPKG_COLLECTION_FIXES,
  VITE_GPKG_COLLECTION_TFRS: import.meta.env.VITE_GPKG_COLLECTION_TFRS,
  VITE_GPKG_COLLECTION_FIR_BOUNDARIES: import.meta.env.VITE_GPKG_COLLECTION_FIR_BOUNDARIES,
  VITE_GPKG_COLLECTION_CIFP_PROCEDURES: import.meta.env.VITE_GPKG_COLLECTION_CIFP_PROCEDURES,
  VITE_GPKG_RASTER_TILE_VFR_SECTIONAL_DATASET: import.meta.env.VITE_GPKG_RASTER_TILE_VFR_SECTIONAL_DATASET,
  VITE_GPKG_RASTER_TILE_VFR_SECTIONAL_LAYER: import.meta.env.VITE_GPKG_RASTER_TILE_VFR_SECTIONAL_LAYER,
  VITE_GPKG_RASTER_TILE_TAC_DATASET: import.meta.env.VITE_GPKG_RASTER_TILE_TAC_DATASET,
  VITE_GPKG_RASTER_TILE_TAC_LAYER: import.meta.env.VITE_GPKG_RASTER_TILE_TAC_LAYER,
  VITE_GPKG_RASTER_TILE_HELI_ROUTE_DATASET: import.meta.env.VITE_GPKG_RASTER_TILE_HELI_ROUTE_DATASET,
  VITE_GPKG_RASTER_TILE_HELI_ROUTE_LAYER: import.meta.env.VITE_GPKG_RASTER_TILE_HELI_ROUTE_LAYER,
  VITE_GPKG_RASTER_TILE_IFR_LOW_DATASET: import.meta.env.VITE_GPKG_RASTER_TILE_IFR_LOW_DATASET,
  VITE_GPKG_RASTER_TILE_IFR_LOW_LAYER: import.meta.env.VITE_GPKG_RASTER_TILE_IFR_LOW_LAYER,
  VITE_GPKG_RASTER_TILE_IFR_HIGH_DATASET: import.meta.env.VITE_GPKG_RASTER_TILE_IFR_HIGH_DATASET,
  VITE_GPKG_RASTER_TILE_IFR_HIGH_LAYER: import.meta.env.VITE_GPKG_RASTER_TILE_IFR_HIGH_LAYER,
  VITE_GPKG_RASTER_TILE_IFR_AREA_DATASET: import.meta.env.VITE_GPKG_RASTER_TILE_IFR_AREA_DATASET,
  VITE_GPKG_RASTER_TILE_IFR_AREA_LAYER: import.meta.env.VITE_GPKG_RASTER_TILE_IFR_AREA_LAYER,
  VITE_GPKG_VECTOR_TILE_AIRPORTS_DATASET: import.meta.env.VITE_GPKG_VECTOR_TILE_AIRPORTS_DATASET,
  VITE_GPKG_VECTOR_TILE_AIRPORTS_LAYER: import.meta.env.VITE_GPKG_VECTOR_TILE_AIRPORTS_LAYER,
  VITE_GPKG_VECTOR_TILE_AIRPORTS_SOURCE_LAYER: import.meta.env.VITE_GPKG_VECTOR_TILE_AIRPORTS_SOURCE_LAYER,
  VITE_GPKG_VECTOR_TILE_NAVAIDS_DATASET: import.meta.env.VITE_GPKG_VECTOR_TILE_NAVAIDS_DATASET,
  VITE_GPKG_VECTOR_TILE_NAVAIDS_LAYER: import.meta.env.VITE_GPKG_VECTOR_TILE_NAVAIDS_LAYER,
  VITE_GPKG_VECTOR_TILE_NAVAIDS_SOURCE_LAYER: import.meta.env.VITE_GPKG_VECTOR_TILE_NAVAIDS_SOURCE_LAYER,
  VITE_GPKG_VECTOR_TILE_OBSTACLES_DATASET: import.meta.env.VITE_GPKG_VECTOR_TILE_OBSTACLES_DATASET,
  VITE_GPKG_VECTOR_TILE_OBSTACLES_LAYER: import.meta.env.VITE_GPKG_VECTOR_TILE_OBSTACLES_LAYER,
  VITE_GPKG_VECTOR_TILE_OBSTACLES_SOURCE_LAYER: import.meta.env.VITE_GPKG_VECTOR_TILE_OBSTACLES_SOURCE_LAYER,
  VITE_GPKG_VECTOR_TILE_AIRSPACES_DATASET: import.meta.env.VITE_GPKG_VECTOR_TILE_AIRSPACES_DATASET,
  VITE_GPKG_VECTOR_TILE_AIRSPACES_LAYER: import.meta.env.VITE_GPKG_VECTOR_TILE_AIRSPACES_LAYER,
  VITE_GPKG_VECTOR_TILE_AIRWAYS_DATASET: import.meta.env.VITE_GPKG_VECTOR_TILE_AIRWAYS_DATASET,
  VITE_GPKG_VECTOR_TILE_AIRWAYS_LAYER: import.meta.env.VITE_GPKG_VECTOR_TILE_AIRWAYS_LAYER,
  VITE_GPKG_VECTOR_TILE_AIRWAYS_SOURCE_LAYER: import.meta.env.VITE_GPKG_VECTOR_TILE_AIRWAYS_SOURCE_LAYER,
};

function env(key: string, defaultValue = ''): string {
  const meta: any = (typeof import.meta !== 'undefined' ? import.meta : {}) as any;
  // `import.meta.env` covers Vite; fall back to `globalThis.process.env` when
  // running under Node/Vitest (no @types/node required — we cast globalThis).
  const g: any = typeof globalThis !== 'undefined' ? (globalThis as any) : {};
  const raw = viteEnv[key] ?? meta?.env?.[key] ?? g?.process?.env?.[key];
  return (raw ?? defaultValue).toString();
}

// ─── Base URLs ───────────────────────────────────────────────────────────────

/** Base URL of the d-TPP / chart_api FastAPI service (port 8000). */
export const CHART_API_BASE: string = env('VITE_CHART_API_BASE', '');
/** Base URL of the gpkg_server FastAPI service (port 8001). */
export const GPKG_SERVER_BASE: string = env('VITE_GPKG_SERVER_BASE', '');
/** Optional bearer token. When blank, requests go out unauthenticated. */
export const DEFAULT_API_TOKEN: string = env('VITE_API_TOKEN', '');

/** Build-time flag — skip SSL verification in dev only. */
export const IS_DEV: boolean = env('DEV', '') === 'true' || env('MODE', '') === 'development';

/** Cesium Ion access token for World Terrain / Photorealistic 3D globe. */
export const CESIUM_ION_TOKEN: string = env('VITE_CESIUM_ION_TOKEN', '');
/** Google Maps Platform key (Map Tiles API, Photorealistic 3D Tiles). */
export const GOOGLE_MAPS_KEY: string = env('VITE_GOOGLE_MAPS_KEY', '');

/** gpkg_server OGC collection ids. Defaults match the sibling local demo GPKGs. */
export const GPKG_COLLECTION_AIRPORTS: string = env('VITE_GPKG_COLLECTION_AIRPORTS', 'airports.am_beacon_pending');
export const GPKG_COLLECTION_RUNWAYS: string = env('VITE_GPKG_COLLECTION_RUNWAYS', 'airports.am_runway_pending');
export const GPKG_COLLECTION_COMMUNICATIONS: string = env('VITE_GPKG_COLLECTION_COMMUNICATIONS', 'communications.communications');
export const GPKG_COLLECTION_AIRSPACES: string = env('VITE_GPKG_COLLECTION_AIRSPACES', 'airspaces.airspaces');
export const GPKG_COLLECTION_NAVAIDS: string = env('VITE_GPKG_COLLECTION_NAVAIDS', 'navaids.navaid');
export const GPKG_COLLECTION_OBSTACLES: string = env('VITE_GPKG_COLLECTION_OBSTACLES', 'obstacles.faa_obstacles');
export const GPKG_COLLECTION_FIXES: string = env('VITE_GPKG_COLLECTION_FIXES', 'fixes.fixes');
export const GPKG_COLLECTION_TFRS: string = env('VITE_GPKG_COLLECTION_TFRS', 'tfrs.tfrs');
export const GPKG_COLLECTION_FIR_BOUNDARIES: string = env('VITE_GPKG_COLLECTION_FIR_BOUNDARIES', 'fir_boundaries.fir_boundaries');
export const GPKG_COLLECTION_CIFP_PROCEDURES: string = env('VITE_GPKG_COLLECTION_CIFP_PROCEDURES', 'cifp.procedures');

/** gpkg_server raster chart table ids. */
export const GPKG_RASTER_TILE_VFR_SECTIONAL_DATASET: string = env('VITE_GPKG_RASTER_TILE_VFR_SECTIONAL_DATASET', 'charts');
export const GPKG_RASTER_TILE_VFR_SECTIONAL_LAYER: string = env('VITE_GPKG_RASTER_TILE_VFR_SECTIONAL_LAYER', 'vfr_sectional');
export const GPKG_RASTER_TILE_TAC_DATASET: string = env('VITE_GPKG_RASTER_TILE_TAC_DATASET', 'charts');
export const GPKG_RASTER_TILE_TAC_LAYER: string = env('VITE_GPKG_RASTER_TILE_TAC_LAYER', 'tac');
export const GPKG_RASTER_TILE_HELI_ROUTE_DATASET: string = env('VITE_GPKG_RASTER_TILE_HELI_ROUTE_DATASET', 'charts');
export const GPKG_RASTER_TILE_HELI_ROUTE_LAYER: string = env('VITE_GPKG_RASTER_TILE_HELI_ROUTE_LAYER', 'heli_route');
export const GPKG_RASTER_TILE_IFR_LOW_DATASET: string = env('VITE_GPKG_RASTER_TILE_IFR_LOW_DATASET', 'charts');
export const GPKG_RASTER_TILE_IFR_LOW_LAYER: string = env('VITE_GPKG_RASTER_TILE_IFR_LOW_LAYER', 'ifr_low');
export const GPKG_RASTER_TILE_IFR_HIGH_DATASET: string = env('VITE_GPKG_RASTER_TILE_IFR_HIGH_DATASET', 'charts');
export const GPKG_RASTER_TILE_IFR_HIGH_LAYER: string = env('VITE_GPKG_RASTER_TILE_IFR_HIGH_LAYER', 'ifr_high');
export const GPKG_RASTER_TILE_IFR_AREA_DATASET: string = env('VITE_GPKG_RASTER_TILE_IFR_AREA_DATASET', 'charts');
export const GPKG_RASTER_TILE_IFR_AREA_LAYER: string = env('VITE_GPKG_RASTER_TILE_IFR_AREA_LAYER', 'ifr_area');

/** gpkg_server vector tile table ids and MVT source-layer names. */
export const GPKG_VECTOR_TILE_AIRPORTS_DATASET: string = env('VITE_GPKG_VECTOR_TILE_AIRPORTS_DATASET', 'airports');
export const GPKG_VECTOR_TILE_AIRPORTS_LAYER: string = env('VITE_GPKG_VECTOR_TILE_AIRPORTS_LAYER', 'faa_airport_movement_vector_tiles');
export const GPKG_VECTOR_TILE_AIRPORTS_SOURCE_LAYER: string = env('VITE_GPKG_VECTOR_TILE_AIRPORTS_SOURCE_LAYER', 'am_beacon_pending');
export const GPKG_VECTOR_TILE_NAVAIDS_DATASET: string = env('VITE_GPKG_VECTOR_TILE_NAVAIDS_DATASET', 'navaids');
export const GPKG_VECTOR_TILE_NAVAIDS_LAYER: string = env('VITE_GPKG_VECTOR_TILE_NAVAIDS_LAYER', 'faa_vhf_nav_vector_tiles');
export const GPKG_VECTOR_TILE_NAVAIDS_SOURCE_LAYER: string = env('VITE_GPKG_VECTOR_TILE_NAVAIDS_SOURCE_LAYER', 'navaid');
export const GPKG_VECTOR_TILE_OBSTACLES_DATASET: string = env('VITE_GPKG_VECTOR_TILE_OBSTACLES_DATASET', 'obstacles');
export const GPKG_VECTOR_TILE_OBSTACLES_LAYER: string = env('VITE_GPKG_VECTOR_TILE_OBSTACLES_LAYER', 'faa_obstacles_tiles');
export const GPKG_VECTOR_TILE_OBSTACLES_SOURCE_LAYER: string = env('VITE_GPKG_VECTOR_TILE_OBSTACLES_SOURCE_LAYER', 'faa_obstacles');
export const GPKG_VECTOR_TILE_AIRSPACES_DATASET: string = env('VITE_GPKG_VECTOR_TILE_AIRSPACES_DATASET', '');
export const GPKG_VECTOR_TILE_AIRSPACES_LAYER: string = env('VITE_GPKG_VECTOR_TILE_AIRSPACES_LAYER', '');
export const GPKG_VECTOR_TILE_AIRWAYS_DATASET: string = env('VITE_GPKG_VECTOR_TILE_AIRWAYS_DATASET', '');
export const GPKG_VECTOR_TILE_AIRWAYS_LAYER: string = env('VITE_GPKG_VECTOR_TILE_AIRWAYS_LAYER', '');
export const GPKG_VECTOR_TILE_AIRWAYS_SOURCE_LAYER: string = env('VITE_GPKG_VECTOR_TILE_AIRWAYS_SOURCE_LAYER', 'airways');

/** True when both Cesium Ion + Google Photorealistic tokens are configured. */
export function isThreeDAvailable(): boolean {
  return CESIUM_ION_TOKEN.length > 0 && GOOGLE_MAPS_KEY.length > 0;
}

// ─── Endpoint configuration ──────────────────────────────────────────────────

export interface EndpointConfig {
  /** d-TPP approach-plate streaming routes served by chart_api. */
  charts: {
    /** `/api/charts/{cycle}/{pdf_name}` */
    pdf: string;
    /** `/api/charts/{cycle}/{pdf_name}/pages/{page}.png` */
    pngPage: string;
    /** `/api/overlays/{cycle}.geojson` */
    overlays: string;
  };
  /** d-TPP chart_api OGC API — Features base paths. */
  ogc: {
    collections: string;
    conformance: string;
  };
  /** gpkg_server OGC API — Features base paths. */
  gpkgOgc: {
    collections: string;
    conformance: string;
  };
  /** STAC API base paths (chart_api). */
  stac: {
    catalog: string;
    search: string;
  };
  /** XYZ tile + TileJSON routes served by gpkg_server. */
  tiles: {
    /** `/api/gpkg/tiles/{dataset}/{layer}/{z}/{x}/{y}` */
    xyz: string;
    /** `/api/gpkg/tiles/{dataset}/{layer}.json` */
    tilejson: string;
    /** `/api/gpkg/terrain/{dataset}/{layer}/{z}/{x}/{y}.png` */
    terrain: string;
  };
  /** Catalog / resource endpoints on gpkg_server. */
  gpkg: {
    datasets: string;
    upload: string;
    styles: string;
    sprites: string;
    fonts: string;
  };
}

const browserOrigin = (): string => (
  typeof window !== 'undefined' && window.location?.origin ? window.location.origin : ''
);

const chart = (path: string) => `${CHART_API_BASE || browserOrigin()}${path}`;
const gpkg = (path: string) => `${GPKG_SERVER_BASE || browserOrigin()}${path}`;
const gpkgOgc = (path: string) => {
  if (GPKG_SERVER_BASE) return `${GPKG_SERVER_BASE}/api/ogc${path}`;
  return `${browserOrigin()}/api/gpkg-ogc${path}`;
};

/** The one-stop endpoint map consumed by services + the OgcServicesContext. */
export const ENDPOINTS: EndpointConfig = {
  charts: {
    pdf:      chart('/api/charts'),
    pngPage:  chart('/api/charts'),
    overlays: chart('/api/overlays'),
  },
  ogc: {
    collections: chart('/api/ogc/collections'),
    conformance: chart('/api/ogc/conformance'),
  },
  gpkgOgc: {
    collections: gpkgOgc('/collections'),
    conformance: gpkgOgc('/conformance'),
  },
  stac: {
    catalog: chart('/api/stac'),
    search:  chart('/api/stac/search'),
  },
  tiles: {
    xyz:      gpkg('/api/gpkg/tiles'),
    tilejson: gpkg('/api/gpkg/tiles'),
    terrain:  gpkg('/api/gpkg/terrain'),
  },
  gpkg: {
    datasets: gpkg('/api/gpkg/datasets'),
    upload:   gpkg('/api/gpkg/datasets/upload'),
    styles:   gpkg('/api/gpkg/styles'),
    sprites:  gpkg('/api/gpkg/sprites'),
    fonts:    gpkg('/api/gpkg/fonts'),
  },
};

/** Convenience builder for a templated XYZ URL against a configured dataset. */
export function buildXyzTileUrl(dataset: string, layer: string, ext = 'png'): string {
  return `${ENDPOINTS.tiles.xyz}/${dataset}/${layer}/{z}/{x}/{y}.${ext}`;
}

export function buildVectorTileUrl(dataset: string, layer: string): string {
  return `${ENDPOINTS.tiles.xyz}/${dataset}/${layer}/{z}/{x}/{y}.pbf`;
}

export function buildTileJsonUrl(dataset: string, layer: string): string {
  return `${ENDPOINTS.tiles.tilejson}/${dataset}/${layer}.json`;
}

export function buildTerrainTileUrl(dataset: string, layer: string, encoding: 'mapbox' | 'terrarium' = 'terrarium'): string {
  return `${ENDPOINTS.tiles.terrain}/${dataset}/${layer}/{z}/{x}/{y}.png?encoding=${encoding}`;
}

export function buildOgcCollectionUrl(collection: string): string {
  return `${ENDPOINTS.gpkgOgc.collections}/${collection}`;
}

export function buildChartOgcCollectionUrl(collection: string): string {
  return `${ENDPOINTS.ogc.collections}/${collection}`;
}

export function buildChartPdfUrl(cycle: string, pdfName: string): string {
  return `${ENDPOINTS.charts.pdf}/${cycle}/${encodeURIComponent(pdfName)}`;
}

export function buildChartPngPageUrl(cycle: string, pdfName: string, pageNumber = 1): string {
  return `${ENDPOINTS.charts.pngPage}/${cycle}/${encodeURIComponent(pdfName)}/pages/${pageNumber}.png`;
}
